import { UsageBar } from "features/data-management/workspace/subscriptions/UsageBar"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { setActiveContextMenu } from "features/popups/slice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useMemo, useCallback, useRef, useState } from "react"
import { WorkspaceCollaboratorProfile } from "../collaborators/WorkspaceCollaboratorProfile"
import * as Icons from "@nova/component-icons"
import { LicenseBadge } from "@nova/ds-component-license-badge"
import { AddOnIcon } from "plugins/AddonIcon"
import { Table } from "@nova/ds-component-table"
import classNames from "classnames"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"
import { updateLicense } from "features/entity-caching/licenses/thunks"
import {
    selectSelectedLicenseIds,
    selectAssignToMemberId,
    resetAssignModal,
    selectAssignToNonMemberEmail,
} from "./slice"
import { get } from "nodash"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import {
    ContextMenu,
    ContextMenuItem,
} from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { selectUserID } from "user/profile/selectors"
import {
    TableData,
    TableBillingCycle,
    useLicensesAndAddonsTableData,
} from "./useLicensesAndAddonsTableData"
import { AssignSubscriptionsModal, UnassignSubscriptionsModal } from "./ManageSubscriptionsModal"
import { SubscriptionActionSelectModal } from "./SubscriptionActionSelectModal"
import { Licenses } from "@nova/constants"
import { selectLicenseById } from "features/entity-caching/licenses/selectors"
import { setLicenseUpgrade } from "features/entity-caching/licenses/actions"
import { initializeCenterModalForComponentAction } from "features/content-overlay/slice"
import { ModalNames } from "features/modals/types"
import { showGlobalModal } from "runlevel/actions"
import { store } from "integrations/redux/store"
import { UserProfileModalTrigger } from "features/users/UserProfileModal"

type SortField = "assignedTo" | "license" | "usage" | "billingCycle"
type SortDir = "ASC" | "DESC"
type SortState = { field: SortField; dir: SortDir }

export const LicensesTable = () => {
    const tableData = useLicensesAndAddonsTableData()
    const [sort, setSort] = useState<SortState>({ field: "assignedTo", dir: "ASC" })

    const handleSort = (field: SortField) => {
        setSort((prev) => {
            if (prev.field !== field) return { field, dir: "ASC" }
            return { field, dir: prev.dir === "ASC" ? "DESC" : "ASC" }
        })
    }

    const sortedTableData = useMemo(() => {
        const compareByAssignedTo = (a: TableData, b: TableData) => {
            if (!a.assigneeId && b.assigneeId) return 1
            if (a.assigneeId && !b.assigneeId) return -1
            return (a.assigneeAttributes?.fullName ?? "").localeCompare(
                b.assigneeAttributes?.fullName ?? ""
            )
        }
        return [...tableData].sort((a, b) => {
            const dir = sort.dir === "ASC" ? 1 : -1
            switch (sort.field) {
                case "assignedTo": {
                    if (!a.assigneeId && b.assigneeId) return 1
                    if (a.assigneeId && !b.assigneeId) return -1
                    return (
                        (a.assigneeAttributes?.fullName ?? "").localeCompare(
                            b.assigneeAttributes?.fullName ?? ""
                        ) * dir
                    )
                }
                case "license": {
                    const aLicense = a.license?.licenseType ?? ""
                    const bLicense = b.license?.licenseType ?? ""
                    return aLicense.localeCompare(bLicense) * dir || compareByAssignedTo(a, b)
                }
                case "usage": {
                    const aUsage = a.usage ?? 0
                    const bUsage = b.usage ?? 0
                    return (aUsage - bUsage) * dir || compareByAssignedTo(a, b)
                }
                case "billingCycle": {
                    return (
                        a.billingCycle.localeCompare(b.billingCycle) * dir ||
                        compareByAssignedTo(a, b)
                    )
                }
            }
        })
    }, [tableData, sort])

    if (!tableData?.length) {
        return (
            <div className="w-full h-full pb-6">
                <div className="w-full h-full border border-gray-700 rounded-xl flex flex-col justify-center items-center gap-2">
                    <small className="xs text-white-400">MY SUBSCRIPTIONS</small>
                    <div className="text-2xl">No subscriptions yet.</div>
                    <div className="flex flex-col gap-4 justify-center items-center max-w-[520px] text-center">
                        <div className="text-gray-100">
                            Licenses and add-ons you’ve purchased or have been assigned to will
                            appear here.
                        </div>
                        <div className="text-gray-100">
                            If {"you're"} having problems with access to add ons or licensees,
                            contact your administrator or{" "}
                            <a
                                className="underline hover:text-blue-500 transition-colors cursor-pointer"
                                href="mailto:hello@mapnova.com"
                            >
                                contact us
                            </a>{" "}
                            for support.
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <Table>
            <Table.Head>
                <Table.Header
                    className="cursor-pointer select-none"
                    onClick={() => handleSort("assignedTo")}
                >
                    <div className="flex items-center gap-1">
                        <div>Assigned to</div>
                        <Table.SortIndicator
                            sortState={sort.field === "assignedTo" ? sort.dir : "none"}
                        />
                    </div>
                </Table.Header>
                <Table.Header
                    className="cursor-pointer select-none"
                    onClick={() => handleSort("license")}
                >
                    <div className="flex items-center gap-1">
                        <div>License</div>
                        <Table.SortIndicator
                            sortState={sort.field === "license" ? sort.dir : "none"}
                        />
                    </div>
                </Table.Header>
                <Table.Header>
                    <div>Add-ons</div>
                </Table.Header>
                <Table.Header
                    className="cursor-pointer select-none"
                    onClick={() => handleSort("usage")}
                >
                    <div className="flex items-center gap-1">
                        <div>Data Usage</div>
                        <Table.SortIndicator
                            sortState={sort.field === "usage" ? sort.dir : "none"}
                        />
                    </div>
                </Table.Header>
                <Table.Header
                    className="cursor-pointer select-none"
                    onClick={() => handleSort("billingCycle")}
                >
                    <div className="flex items-center gap-1">
                        <div>Billing Cycle</div>
                        <Table.SortIndicator
                            sortState={sort.field === "billingCycle" ? sort.dir : "none"}
                        />
                    </div>
                </Table.Header>
                <Table.Header></Table.Header>
            </Table.Head>
            <Table.Body>
                {sortedTableData.map((data, i) => (
                    <LicenseTableRow key={data.assigneeId || i} data={data} />
                ))}
            </Table.Body>
        </Table>
    )
}

const LicenseTableRow = ({ data }: { data: TableData }) => {
    const dispatch = useAppDispatch()
    const ownerId = useAppSelector(selectUserID)
    const confirmationModal = useConfirmationModalContext()
    const selectedLicenseIds = useAppSelector(selectSelectedLicenseIds)
    const assignToMemberId = useAppSelector(selectAssignToMemberId)
    const assignToNonMemberEmail = useAppSelector(selectAssignToNonMemberEmail)
    const selectedLicenseIdsRef = useRef(selectedLicenseIds)
    selectedLicenseIdsRef.current = selectedLicenseIds
    const assignToMemberIdRef = useRef(assignToMemberId)
    assignToMemberIdRef.current = assignToMemberId
    const assignToNonMemberEmailRef = useRef(assignToNonMemberEmail)
    assignToNonMemberEmailRef.current = assignToNonMemberEmail
    const handleOpenTransferConfirmationModal = useCallback(async () => {
        dispatch(resetAssignModal())
        await confirmationModal.confirm({
            RenderComponent: ({ confirm, cancel, isPerformingAsyncConfirmCallback }) => (
                <AssignSubscriptionsModal
                    confirm={confirm}
                    cancel={cancel}
                    currentAssigneeId={data.assigneeId}
                    isPerformingAsyncConfirmCallback={isPerformingAsyncConfirmCallback}
                />
            ),
            asyncConfirmCallback: async () => {
                const licenseIds = selectedLicenseIdsRef.current
                const assigneeId = assignToMemberIdRef.current || assignToNonMemberEmailRef.current
                if (!licenseIds.length || !ownerId) return
                const assignTo = assigneeId || "unassigned"
                const results = await Promise.all(
                    licenseIds.map((licenseId) =>
                        dispatch(
                            updateLicense({
                                ownerId,
                                licenseId,
                                attributes: { assigneeId: assignTo },
                            })
                        ).catch()
                    )
                )
                let duplicatedCount = 0
                results.forEach((res) => {
                    if (!get(res, "error")) return
                    if (get(res, "meta.rejectionReason") !== "duplicateSubscription") return
                    duplicatedCount++
                })
                if (duplicatedCount === licenseIds.length) {
                    dispatch(
                        setGlobalNotificationContext({
                            status: GlobalNotificationStatus.error,
                            message: `Failed to assign ${duplicatedCount} subscription${duplicatedCount > 1 ? "s" : ""}. This person already has ${duplicatedCount > 1 ? "these" : "this"} subscription${duplicatedCount > 1 ? "s" : ""}.`,
                            timeoutInMs: 3000,
                        })
                    )
                    return
                }
                if (duplicatedCount > 0) {
                    const successCount = licenseIds.length - duplicatedCount
                    dispatch(
                        setGlobalNotificationContext({
                            status: GlobalNotificationStatus.warning,
                            message: `Successfully assigned ${successCount} subscription${successCount > 1 ? "s" : ""} and failed to assign ${duplicatedCount} subscription${duplicatedCount > 1 ? "s" : ""}, please try again.`,
                            timeoutInMs: 3000,
                        })
                    )
                    return
                }
                dispatch(
                    setGlobalNotificationContext({
                        status: GlobalNotificationStatus.success,
                        message: `${licenseIds.length} subscription${licenseIds.length > 1 ? "s" : ""} assigned successfully.`,
                        timeoutInMs: 3000,
                    })
                )
                dispatch(resetAssignModal())
            },
        })
    }, [confirmationModal, data.assigneeId, dispatch, ownerId])
    const handleOpenUnassignConfirmationModal = useCallback(async () => {
        if (!data.assigneeId) return
        dispatch(resetAssignModal())
        await confirmationModal.confirm({
            RenderComponent: ({ confirm, cancel, isPerformingAsyncConfirmCallback }) => (
                <UnassignSubscriptionsModal
                    confirm={confirm}
                    cancel={cancel}
                    currentAssigneeId={data.assigneeId!}
                    isPerformingAsyncConfirmCallback={isPerformingAsyncConfirmCallback}
                />
            ),
            asyncConfirmCallback: async () => {
                const licenseIds = selectedLicenseIdsRef.current
                if (!licenseIds.length || !ownerId) return
                await Promise.all(
                    licenseIds.map((licenseId) =>
                        dispatch(
                            updateLicense({
                                ownerId,
                                licenseId,
                                attributes: { assigneeId: "unassigned" },
                            })
                        ).catch()
                    )
                )
                dispatch(
                    setGlobalNotificationContext({
                        status: GlobalNotificationStatus.success,
                        message: `${licenseIds.length} subscription${licenseIds.length > 1 ? "s" : ""} unassigned successfully.`,
                        timeoutInMs: 3000,
                    })
                )
                dispatch(resetAssignModal())
            },
        })
    }, [confirmationModal, data.assigneeId, dispatch, ownerId])
    const activeLicenseMenuContextMenu = useAppSelector(selectActiveContextMenu("LicenseMenu"))
    const rowKey = data.license?.licenseId ?? data.addOns[0]?.licenseId
    const contextMenuIsForThisItem = useMemo(() => {
        if (!activeLicenseMenuContextMenu) return false
        if (activeLicenseMenuContextMenu?.data.isUnassigned) {
            return rowKey === activeLicenseMenuContextMenu.data.licenseId
        }
        return activeLicenseMenuContextMenu?.data.assigneeId === data.assigneeId
    }, [activeLicenseMenuContextMenu, data.assigneeId, rowKey])
    const singleSubscriptionId = useMemo(() => {
        if (data.license && data.addOns.length === 0) return data.license.subscriptionId
        if (!data.license && data.addOns.length === 1) return data.addOns[0].subscriptionId
        return null
    }, [data.license, data.addOns])
    const contextMenuSections: ContextMenuItem[][] = useMemo(() => {
        const sections: ContextMenuItem[][] = [
            [
                {
                    title: `${data.assigneeId ? "Transfer" : "Assign"} Subscription${!singleSubscriptionId ? "s" : ""}`,
                    Icon: Icons.PersonEdit,
                    onClick: handleOpenTransferConfirmationModal,
                },
            ],
            [
                ...(data.license?.licenseType === Licenses.Essentials
                    ? [
                          {
                              title: "Upgrade License",
                              Icon: Icons.PersonUpgrade,
                              onClick: () => {
                                  const licenseModel = selectLicenseById(data.license!.licenseId)(
                                      store.getState()
                                  )
                                  dispatch(setLicenseUpgrade(licenseModel))
                                  dispatch(
                                      initializeCenterModalForComponentAction(
                                          ModalNames.LicenseUpgradeModal
                                      )
                                  )
                                  dispatch(showGlobalModal())
                              },
                          },
                      ]
                    : []),
                {
                    title: `View Invoice${!singleSubscriptionId ? "s" : ""}`,
                    Icon: Icons.Invoice,
                    onClick: async () => {
                        if (singleSubscriptionId) {
                            try {
                                const res = await fetch(
                                    `/api/stripe/stripe-get-invoice-url?subscriptionId=${singleSubscriptionId}`
                                ).then((r) => r.json())
                                window.open(res.url, "_blank")
                            } catch {
                                dispatch(
                                    setGlobalNotificationContext({
                                        status: GlobalNotificationStatus.error,
                                        message: "Could not get invoice",
                                        timeoutInMs: 3000,
                                    })
                                )
                            }
                        } else {
                            await confirmationModal.confirm({
                                RenderComponent: ({ cancel }) => (
                                    <SubscriptionActionSelectModal
                                        title="View Invoice"
                                        license={data.license}
                                        addOns={data.addOns}
                                        cancel={cancel}
                                        onSelect={async (license) => {
                                            try {
                                                if (!license?.subscriptionId) throw new Error()
                                                const res = await fetch(
                                                    `/api/stripe/stripe-get-invoice-url?subscriptionId=${license.subscriptionId}`
                                                ).then((r) => r.json())
                                                window.open(res.url, "_blank")
                                            } catch {
                                                dispatch(
                                                    setGlobalNotificationContext({
                                                        status: GlobalNotificationStatus.error,
                                                        message: "Could not get invoice",
                                                        timeoutInMs: 3000,
                                                    })
                                                )
                                            }
                                        }}
                                    />
                                ),
                            })
                        }
                    },
                },
                {
                    title: `Manage Billing Account${!singleSubscriptionId ? "s" : ""}`,
                    Icon: Icons.ManageAccounts,
                    onClick: async () => {
                        if (singleSubscriptionId) {
                            try {
                                const res = await fetch(
                                    `/api/stripe/stripe-create-customer-portal?subscriptionId=${singleSubscriptionId}&redirectUrl=${encodeURIComponent(window.location.href)}`
                                ).then((r) => r.json())
                                window.open(res.url, "_blank")
                            } catch {
                                dispatch(
                                    setGlobalNotificationContext({
                                        status: GlobalNotificationStatus.error,
                                        message: "Could not get billing account",
                                        timeoutInMs: 3000,
                                    })
                                )
                            }
                        } else {
                            await confirmationModal.confirm({
                                RenderComponent: ({ cancel }) => (
                                    <SubscriptionActionSelectModal
                                        title="Manage Billing Account"
                                        license={data.license}
                                        addOns={data.addOns}
                                        cancel={cancel}
                                        onSelect={async (license) => {
                                            try {
                                                if (!license?.subscriptionId) throw new Error()
                                                const res = await fetch(
                                                    `/api/stripe/stripe-create-customer-portal?subscriptionId=${license.subscriptionId}&redirectUrl=${encodeURIComponent(window.location.href)}`
                                                ).then((r) => r.json())
                                                window.open(res.url, "_blank")
                                            } catch {
                                                dispatch(
                                                    setGlobalNotificationContext({
                                                        status: GlobalNotificationStatus.error,
                                                        message: "Could not get billing account",
                                                        timeoutInMs: 3000,
                                                    })
                                                )
                                            }
                                        }}
                                    />
                                ),
                            })
                        }
                    },
                },
            ],
        ]
        if (data.assigneeId) {
            sections.push([
                {
                    title: `Unassign Subscription${!singleSubscriptionId ? "s" : ""}`,
                    Icon: Icons.PersonRemove,
                    onClick: handleOpenUnassignConfirmationModal,
                    isDestructive: true,
                },
            ])
        }
        return sections
    }, [
        data.assigneeId,
        data.license,
        data.addOns,
        handleOpenTransferConfirmationModal,
        singleSubscriptionId,
        dispatch,
        confirmationModal,
        handleOpenUnassignConfirmationModal,
    ])
    return (
        <>
            <Table.Row className="group">
                <Table.Cell>
                    <UserProfileModalTrigger userId={data.assigneeId}>
                        <WorkspaceCollaboratorProfile
                            userId={data.assigneeId}
                            email={data.assigneeAttributes?.email}
                            fullName={
                                data.assigneeAttributes?.fullName || data.assigneeId || "Unassigned"
                            }
                        />
                    </UserProfileModalTrigger>
                </Table.Cell>
                <Table.Cell>
                    {data.license && <LicenseBadge license={data.license.licenseType} />}
                </Table.Cell>
                <Table.Cell>
                    <div className="flex gap-1 items-center">
                        {data.addOns.map((addon) => (
                            <AddOnIcon key={addon.licenseId} addOn={addon.addOnType} />
                        ))}
                    </div>
                </Table.Cell>
                <Table.Cell>
                    {data.license && typeof data.usage !== "undefined" && (
                        <UsageBar
                            license={{
                                usageAmount: data.usage,
                                subscriptionType: data.license.licenseType,
                            }}
                            isSelf={data.assigneeId === ownerId}
                        />
                    )}
                </Table.Cell>
                <Table.Cell>{formatBillingCycle(data.billingCycle)}</Table.Cell>
                <Table.Cell>
                    <div
                        className={classNames("flex relative gap-2", {
                            "opacity-0 group-hover:opacity-100": !contextMenuIsForThisItem,
                        })}
                    >
                        <Icons.SmallIcon
                            className="cursor-pointer text-gray-100 hover:text-gray-0"
                            Icon={!!data.assigneeId ? Icons.PersonEdit : Icons.PersonAdd}
                            onClick={handleOpenTransferConfirmationModal}
                        />
                        <Icons.SmallIcon
                            className="cursor-pointer text-gray-100 hover:text-gray-0"
                            Icon={Icons.ThreeDotsSettings}
                            onClick={(e) => {
                                if (activeLicenseMenuContextMenu) {
                                    dispatch(setActiveContextMenu(undefined))
                                    return
                                }
                                dispatch(
                                    setActiveContextMenu({
                                        type: "LicenseMenu",
                                        data: data.assigneeId
                                            ? { assigneeId: data.assigneeId }
                                            : {
                                                  isUnassigned: true,
                                                  licenseId: rowKey || "",
                                              },
                                        position: { left: e.clientX, top: e.clientY + 12 },
                                    })
                                )
                            }}
                        />
                    </div>
                </Table.Cell>
                {activeLicenseMenuContextMenu && contextMenuIsForThisItem && (
                    <ContextMenu
                        onBlur={() => dispatch(setActiveContextMenu(undefined))}
                        sections={contextMenuSections}
                        isFixedPosition={true}
                        top={activeLicenseMenuContextMenu.position.top}
                        left={activeLicenseMenuContextMenu.position.left}
                    />
                )}
            </Table.Row>
        </>
    )
}

const formatBillingCycle = (billingCycle: TableBillingCycle) => {
    switch (billingCycle) {
        case "year":
            return "Annual"
        case "month":
            return "Monthly"
        default:
            return "Annual/Monthly"
    }
}
